import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, Phone, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useSubmitLead } from '@/hooks/useSubmitLead';
import { toast } from 'sonner';
import { usePublicSiteSettings } from '@/hooks/usePublicSiteSettings';
import type { Property } from '@/types/property';

const contactSchema = z.object({
  name: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(80, 'Nome muito longo'),
  email: z.string().trim().email('Email inválido').max(120, 'Email muito longo'),
  phone: z.string().trim().max(30, 'Telefone muito longo').optional().or(z.literal('')),
  message: z.string().trim().max(500, 'Mensagem muito longa').optional(),
  website: z.string().optional(), // honeypot
});

type ContactFormData = z.infer<typeof contactSchema>;

interface PropertyContactFormProps {
  property: Property;
}

export function PropertyContactForm({ property }: PropertyContactFormProps) {
  const { submitLead, isSubmitting } = useSubmitLead();
  const { data: settings } = usePublicSiteSettings();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: `Olá! Tenho interesse no imóvel "${property.title}" e gostaria de mais informações.`,
      website: '', // honeypot
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    const result = await submitLead({
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      message: data.message || undefined,
      property_id: property.id,
      source: 'property_page',
    });

    if (result.success) {
      toast.success(result.message);
      form.reset();
    } else if (result.isRateLimited) {
      toast.warning(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const handleWhatsApp = () => {
    if (!settings?.whatsapp) {
      toast.error('WhatsApp não configurado');
      return;
    }
    const phone = settings.whatsapp.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Olá! Tenho interesse no imóvel "${property.title}" (${window.location.href}) e gostaria de mais informações.`
    );
    window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
  };

  const handleCall = () => {
    if (!settings?.phone) {
      toast.error('Telefone não configurado');
      return;
    }
    const phone = settings.phone.replace(/\D/g, '');
    window.open(`tel:+55${phone}`, '_self');
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-6 text-lg font-bold text-foreground">Tenho Interesse</h3>

      {/* Quick Actions */}
      <div className="mb-6 flex gap-3">
        <Button 
          variant="outline" 
          className="flex-1 gap-2"
          onClick={handleWhatsApp}
        >
          <MessageCircle className="h-4 w-4" />
          WhatsApp
        </Button>
        <Button 
          variant="outline" 
          className="flex-1 gap-2"
          onClick={handleCall}
        >
          <Phone className="h-4 w-4" />
          Ligar
        </Button>
      </div>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">ou envie uma mensagem</span>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Honeypot field - hidden from users */}
          <div className="absolute left-[-9999px]" aria-hidden="true">
            <input
              type="text"
              {...form.register('website')}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome *</FormLabel>
                <FormControl>
                  <Input placeholder="Seu nome completo" maxLength={80} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="seu@email.com" maxLength={120} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(00) 00000-0000" maxLength={30} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mensagem</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Escreva sua mensagem..." 
                    className="min-h-[100px] resize-none"
                    maxLength={500}
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full gap-2"
            disabled={isSubmitting}
          >
            <Send className="h-4 w-4" />
            {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
          </Button>
        </form>
      </Form>
    </div>
  );
}